import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserService } from '../service/user.service';

/** Tempo de inatividade para exibir o aviso e realizar logout (30 min). */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

/** Chaves do localStorage para sincronização entre abas */
const STORAGE_KEY_LAST_ACTIVITY = 'idle_last_activity';
const STORAGE_KEY_LOGOUT_EVENT = 'idle_logout_event';

/** Canal de comunicação entre abas/janelas */
const BROADCAST_CHANNEL_NAME = 'idle_timeout_channel';

interface BroadcastMessage {
  type: 'activity' | 'logout';
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private warningTimer: any;
  private lastActivity: number = Date.now();
  private broadcastChannel: BroadcastChannel | null = null;
  private storageEventListener: ((event: StorageEvent) => void) | null = null;

  private isIdle$ = new BehaviorSubject<boolean>(false);
  private showWarning$ = new BehaviorSubject<boolean>(false);
  private timeRemaining$ = new BehaviorSubject<number>(0);

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private userService: UserService
  ) {}

  startWatching() {
    // Ler última atividade do localStorage (sincronizada entre abas)
    const storedActivity = localStorage.getItem(STORAGE_KEY_LAST_ACTIVITY);
    if (storedActivity) {
      this.lastActivity = parseInt(storedActivity, 10);
    } else {
      this.lastActivity = Date.now();
      this.updateLastActivityInStorage(this.lastActivity);
    }

    this.setupEventListeners();
    this.setupBroadcastChannel();
    this.setupStorageListener();

    // Verificar se já passou o tempo de inatividade
    const timeSinceLastActivity = Date.now() - this.lastActivity;
    if (timeSinceLastActivity >= IDLE_TIMEOUT_MS) {
      // Já está inativo, fazer logout imediatamente
      this.onTimeout();
    } else {
      // Iniciar timer com o tempo restante
      this.resetTimer();
    }
  }

  stopWatching() {
    this.clearTimers();
    this.removeEventListeners();
    this.closeBroadcastChannel();
    this.removeStorageListener();
  }

  resetTimer() {
    this.lastActivity = Date.now();
    this.updateLastActivityInStorage(this.lastActivity);
    this.clearTimers();
    this.showWarning$.next(false);
    this.isIdle$.next(false);
    this.startTimers();

    // Notificar outras abas sobre a atividade
    this.broadcastActivity();
  }

  getIdleState(): Observable<boolean> {
    return this.isIdle$.asObservable();
  }

  getWarningState(): Observable<boolean> {
    return this.showWarning$.asObservable();
  }

  getTimeRemaining(): Observable<number> {
    return this.timeRemaining$.asObservable();
  }

  private setupEventListeners() {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    this.ngZone.runOutsideAngular(() => {
      events.forEach(event => {
        document.addEventListener(event, this.onUserActivity.bind(this), true);
      });
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange.bind(this));
  }

  private removeEventListeners() {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    events.forEach(event => {
      document.removeEventListener(event, this.onUserActivity.bind(this), true);
    });

    document.removeEventListener('visibilitychange', this.onVisibilityChange.bind(this));
  }

  private setupBroadcastChannel() {
    // Verificar se BroadcastChannel é suportado
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        this.broadcastChannel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
          this.ngZone.run(() => {
            this.handleBroadcastMessage(event.data);
          });
        };
      } catch (error) {
        console.warn('BroadcastChannel não disponível, usando apenas localStorage', error);
      }
    }
  }

  private closeBroadcastChannel() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }

  private setupStorageListener() {
    // Listener para storage events (fallback e suporte adicional)
    this.storageEventListener = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY_LOGOUT_EVENT && event.newValue) {
        this.ngZone.run(() => {
          // Outra aba fez logout, fazer logout nesta aba também
          this.handleLogoutFromAnotherTab();
        });
      } else if (event.key === STORAGE_KEY_LAST_ACTIVITY && event.newValue) {
        // Outra aba teve atividade, resetar timer local
        this.ngZone.run(() => {
          const newTimestamp = parseInt(event.newValue!, 10);
          if (newTimestamp > this.lastActivity) {
            this.lastActivity = newTimestamp;
            this.clearTimers();
            this.showWarning$.next(false);
            this.isIdle$.next(false);
            this.startTimers();
          }
        });
      }
    };
    window.addEventListener('storage', this.storageEventListener);
  }

  private removeStorageListener() {
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
      this.storageEventListener = null;
    }
  }

  private handleBroadcastMessage(message: BroadcastMessage) {
    if (message.type === 'activity') {
      // Outra aba teve atividade, resetar timer local
      if (message.timestamp > this.lastActivity) {
        this.lastActivity = message.timestamp;
        this.clearTimers();
        this.showWarning$.next(false);
        this.isIdle$.next(false);
        this.startTimers();
      }
    } else if (message.type === 'logout') {
      // Outra aba fez logout, fazer logout nesta aba também
      this.handleLogoutFromAnotherTab();
    }
  }

  private broadcastActivity() {
    if (this.broadcastChannel) {
      try {
        const message: BroadcastMessage = {
          type: 'activity',
          timestamp: this.lastActivity
        };
        this.broadcastChannel.postMessage(message);
      } catch (error) {
        console.warn('Erro ao enviar mensagem via BroadcastChannel', error);
      }
    }
  }

  private broadcastLogout() {
    if (this.broadcastChannel) {
      try {
        const message: BroadcastMessage = {
          type: 'logout',
          timestamp: Date.now()
        };
        this.broadcastChannel.postMessage(message);
      } catch (error) {
        console.warn('Erro ao enviar logout via BroadcastChannel', error);
      }
    }

    // Também usar localStorage como fallback
    localStorage.setItem(STORAGE_KEY_LOGOUT_EVENT, Date.now().toString());
  }

  private updateLastActivityInStorage(timestamp: number) {
    localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, timestamp.toString());
  }

  private onUserActivity() {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivity;

    if (timeSinceLastActivity > 1000) {
      this.ngZone.run(() => {
        this.resetTimer();
      });
    }
  }

  private onVisibilityChange() {
    this.ngZone.run(() => {
      if (!document.hidden) {
        // Aba voltou a ficar visível, verificar se houve inatividade
        const storedActivity = localStorage.getItem(STORAGE_KEY_LAST_ACTIVITY);
        if (storedActivity) {
          const lastActivityTimestamp = parseInt(storedActivity, 10);
          const timeSinceLastActivity = Date.now() - lastActivityTimestamp;

          if (timeSinceLastActivity >= IDLE_TIMEOUT_MS) {
            // Já passou o tempo de inatividade, fazer logout imediatamente
            this.onTimeout();
            return;
          } else {
            // Ainda não passou o tempo, ajustar timer com tempo restante
            this.lastActivity = lastActivityTimestamp;
            this.clearTimers();
            this.startTimers();
          }
        }
      }
      // Não pausar os timers quando a aba fica oculta
      // Os timers continuam rodando em background
    });
  }

  private startTimers() {
    // Calcular tempo restante baseado na última atividade
    const timeSinceLastActivity = Date.now() - this.lastActivity;
    const timeRemaining = Math.max(0, IDLE_TIMEOUT_MS - timeSinceLastActivity);

    if (timeRemaining === 0) {
      // Já passou o tempo, fazer logout imediatamente
      this.onTimeout();
      return;
    }

    this.warningTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.showWarning$.next(true);
        this.startCountdown();
        this.onTimeout();
      });
    }, timeRemaining);
  }

  private startCountdown() {
    this.timeRemaining$.next(0);
  }

  private clearTimers() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  private onTimeout() {
    this.isIdle$.next(true);
    this.showWarning$.next(false);
    this.clearTimers();

    // Notificar outras abas sobre o logout
    this.broadcastLogout();

    // Fazer logout
    this.userService.logout();

    // Limpar dados de sincronização
    this.cleanupStorage();

    this.navigateToLogin();
  }

  private handleLogoutFromAnotherTab() {
    this.isIdle$.next(true);
    this.showWarning$.next(false);
    this.clearTimers();

    this.userService.logout();
    this.cleanupStorage();
    this.navigateToLogin();
  }

  private getReturnUrl(): string | undefined {
    const currentUrl = this.router.url;
    if (currentUrl.startsWith('/login')) {
      const match = currentUrl.match(/[?&]returnUrl=([^&]+)/);
      return match ? decodeURIComponent(match[1]) : undefined;
    }
    return currentUrl;
  }

  private navigateToLogin() {
    const returnUrl = this.getReturnUrl();
    this.router.navigate(['/login'], {
      queryParams: {
        reason: 'inactivity',
        ...(returnUrl ? { returnUrl } : {})
      }
    });
  }

  private cleanupStorage() {
    // Limpar dados do localStorage relacionados ao idle timeout
    localStorage.removeItem(STORAGE_KEY_LAST_ACTIVITY);
    localStorage.removeItem(STORAGE_KEY_LOGOUT_EVENT);
  }
}
